import React, { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import useTranslation from '~/app/hooks/useTranslation';
import Icon from '~/app/common/Icon';
import LightBoxImage from '~/app/common/LightBoxImage';
import ConfirmationMenu from '~/app/common/ConfirmationMenu';
import File from '~/app/modules/form/File';
import { Attachment as WorkOrderAttachment } from '~/workorder/types';
import {
    downloadAttachment,
    isAllowedAttachment,
    isAttachmentImage,
} from './helper';
import classes from './style.module.scss';
import Loader from '~/app/common/Loader';
import { notify } from '~/app/helpers/notify';
import classNames from 'classnames';

export interface AttachmentProps {
    attachments: WorkOrderAttachment[];
    maxFiles: number;
    maxFileSize: number;
    allowedFileExtensions: string[];
    canEdit: boolean;
    saving: boolean;
    onAdd: (files: File[]) => void | Promise<void>;
    onDelete: (attachment: WorkOrderAttachment) => void | Promise<void>;
}

export type AttachmentComponent = React.FC<AttachmentProps>;

const Attachment: AttachmentComponent = ({ attachments, maxFiles, maxFileSize, allowedFileExtensions, canEdit, saving, onAdd, onDelete }) => {
    const { t } = useTranslation('workorder');
    const { t: tParts } = useTranslation('parts');
    const [expanded, setExpanded] = useState(false);
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
    const [attachmentToDelete, setAttachmentToDelete] = useState<WorkOrderAttachment | null>(null);
    const [downloadingId, setDownloadingId] = useState<number | null>(null);
    const [filePickerKey, setFilePickerKey] = useState(0);
    const fileInputRef = useRef<any>(null);

    const { images, others, count } = useMemo(() => {
        const images: WorkOrderAttachment[] = [];
        const others: WorkOrderAttachment[] = [];
        attachments.forEach(item => {
            if (isAttachmentImage(item.mimeType)) {
                images.push(item);
            } else {
                others.push(item);
            }
        });
        return { images, others, count: attachments.length };
    }, [attachments]);

    const remainingSlots = maxFiles - count;

    const resetFilePicker = useCallback(() => {
        setFilePickerKey(prev => prev + 1);
    }, []);

    const handleAddClick = useCallback((event: React.MouseEvent) => {
        if (!canEdit || saving || remainingSlots <= 0) {
            return;
        }
        fileInputRef.current?.triggerFileInputClick(event);
    }, [canEdit, saving, remainingSlots]);

    const handleFilesChange = useCallback((event: any) => {
        const newFiles: File[] = event.target.value ?? [];

        if (!newFiles.length || !canEdit) {
            return;
        }

        if (newFiles.length > remainingSlots) {
            toast.error(t('detail.attachmentMaxFiles', { max: maxFiles }));
            resetFilePicker();
            return;
        }

        const hasInvalid = newFiles.some(file => !isAllowedAttachment(file.name, allowedFileExtensions));
        if (hasInvalid) {
            toast.error(t('detail.attachmentInvalidType'));
            resetFilePicker();
            return;
        }

        const maxFileSizeBytes = maxFileSize * 1024 * 1024;
        const oversizedIndex = newFiles.findIndex(f => f.size > maxFileSizeBytes);
        if (oversizedIndex !== -1) {
            fileInputRef.current?.handleImageDelete(oversizedIndex);
            toast.error(t('detail.attachmentSizeError', { max: maxFileSize }));
            return;
        }

        Promise.resolve(onAdd(newFiles)).finally(() => resetFilePicker());
    }, [canEdit, remainingSlots, onAdd, t, resetFilePicker, maxFileSize, maxFiles, allowedFileExtensions]);

    const onDeleteConfirm = useCallback((attachment: WorkOrderAttachment) => {
        setAttachmentToDelete(attachment);
        setIsConfirmationOpen(true);
    }, []);

    const handleDelete = useCallback(() => {
        if (!attachmentToDelete) {
            setIsConfirmationOpen(false);
            setAttachmentToDelete(null);
            return;
        }

        const attachment = attachmentToDelete;
        setIsConfirmationOpen(false);
        setAttachmentToDelete(null);
        onDelete(attachment);
    }, [attachmentToDelete, onDelete]);

    const handleDownload = useCallback(async (attachment: WorkOrderAttachment, event?: React.MouseEvent) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        if (!attachment.url || downloadingId !== null || saving) {
            return;
        }

        setDownloadingId(attachment.id);
        try {
            await downloadAttachment(
                attachment.url,
                attachment.name,
                attachment.mimeType
            );
            notify(
                t(window['cordova']?.platformId === 'ios'
                    ? 'detail.attachmentDownloadSuccessIos'
                    : 'detail.attachmentDownloadSuccess'),
                'success'
            );
        } catch {
            notify(t('detail.attachmentDownloadError'), 'error');
        } finally {
            setDownloadingId(null);
        }
    }, [downloadingId, saving, t]);

    const stopPropagation = useCallback((event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
    }, []);

    const showAdd = expanded && canEdit && remainingSlots > 0;

    return (
        <div className={classes['card']}>
            {saving && <Loader size="20" />}
            <div className={classes['header']} onClick={() => setExpanded(prev => !prev)}>
                <h2 className={classes['header-title']}>
                    <Icon name="description" size={20} className={classes['header-icon']} />
                    {t('detail.attachments')} ({count}/{maxFiles})
                </h2>
                <Icon name={expanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'} className={classes['chevron']}/>
            </div>

            {expanded && (
                count === 0 ? (
                    <p className={classes['empty']}>{t('detail.noAttachmentAdded')}</p>
                ) : (
                    <>
                        {images.length > 0 && (
                            <div className={classes['section']}>
                                <p className={classes['section-label']}>{t('detail.attachmentImages')}</p>
                                <div className={classes['attachment-images']}>
                                    {images.map(image => (
                                        <div key={image.id} className={classes['workorder-image']}>
                                            <LightBoxImage image={image.url} download={true} />
                                            {canEdit && (
                                                <button
                                                    type="button"
                                                    className={classes['image-delete-button']}
                                                    disabled={saving}
                                                    onClick={(event) => {
                                                        stopPropagation(event);
                                                        onDeleteConfirm(image);
                                                    }}
                                                >
                                                    <Icon name="remove_circle" />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {others.length > 0 && (
                            <div className={classes['section']}>
                                <p className={classes['section-label']}>{t('detail.attachmentOthers')}</p>
                                <div className={classes['pdf-preview-list']}>
                                    {others.map(pdf => (
                                        <div key={pdf.id} className={classes['pdf-preview-row']}>
                                            <Icon name="description" className={classes['pdf-icon']} />
                                            <div className={classes['pdf-info']}>
                                                <div className={classes['pdf-name']}>{pdf.name}</div>
                                            </div>
                                            <button
                                                type="button"
                                                className={classNames(classes['action-button'], classes['download-button'])}
                                                disabled={saving || downloadingId !== null}
                                                onClick={() => handleDownload(pdf)}
                                            >
                                                {downloadingId === pdf.id ? (
                                                    <Loader size="16" inline />
                                                ) : (
                                                    <Icon name="download" />
                                                )}
                                            </button>
                                            {canEdit && (
                                                <button
                                                    type="button"
                                                    className={classes['action-button']}
                                                    disabled={saving}
                                                    onClick={() => onDeleteConfirm(pdf)}
                                                >
                                                    <Icon name="delete" />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )
            )}

            {showAdd && (
                <div className={classes['add-action-container']}>
                    <button
                        type="button"
                        className={classes['add-action']}
                        disabled={saving}
                        onClick={handleAddClick}
                    >
                        + {tParts('add')}
                    </button>
                    <File
                        key={filePickerKey}
                        ref={fileInputRef}
                        name="attachments"
                        onlyImage={false}
                        showInput={false}
                        preview={false}
                        imageEdit={false}
                        maxFiles={remainingSlots}
                        accept={allowedFileExtensions.map(ext => `.${ext}`).join(',')}
                        onChange={handleFilesChange}
                    />
                </div>
            )}

            <ConfirmationMenu
                isOpen={isConfirmationOpen}
                onConfirm={handleDelete}
                onCancel={() => {
                    setIsConfirmationOpen(false);
                    setAttachmentToDelete(null);
                }}
                confirmationText={t('detail.attachmentDeleteConfirm')}
            />
        </div>
    );
};

export default Attachment;
